/** Os 3 canais Android semânticos (criados no boot do app, ANTES do 1º push). */
export const PUSH_ANDROID_CHANNELS = [
    {
        id: 'lume-urgent',
        name: 'Urgente',
        importance: 'max',
        description: 'Precisa de você agora: transferência, atribuição, incidente, SLA.',
    },
    {
        id: 'lume-work',
        name: 'Trabalho',
        importance: 'default',
        description: 'Eventos do seu trabalho: mensagens de colegas, reatribuições, pendências.',
    },
    {
        id: 'lume-ambient',
        name: 'Acompanhamento',
        importance: 'low',
        description: 'Informativo, sem pressa: insights, decisões de override, coaching.',
    },
];
/** Categorias iOS com ações (v1: só o handoff; ações abrem a conversa — deep-link). */
export const PUSH_IOS_CATEGORIES = [
    {
        id: 'lume.handoff',
        actions: [
            { id: 'assumir', title: 'Assumir' },
            { id: 'passar', title: 'Passar' },
        ],
    },
];
const urgent = (categoryId) => ({
    tier: 'urgent',
    iosInterruptionLevel: 'time-sensitive',
    androidChannelId: 'lume-urgent',
    priority: 'high',
    offShiftBehavior: 'suppressible',
    ...(categoryId ? { categoryId } : {}),
});
const urgentCompliance = { ...urgent(), offShiftBehavior: 'never-suppress' };
const work = {
    tier: 'work',
    iosInterruptionLevel: 'active',
    androidChannelId: 'lume-work',
    priority: 'high',
    offShiftBehavior: 'suppressible',
};
const ambient = {
    tier: 'ambient',
    iosInterruptionLevel: 'passive',
    androidChannelId: 'lume-ambient',
    priority: 'normal',
    offShiftBehavior: 'suppressible',
};
/**
 * Matriz canônica dos templates (spec §4-N1, reconciliada INV-N1). Mantida em
 * sincronia com `apps/engine/prisma/seeds/notification-templates.ts` — o
 * `toHaveLength` do spec ao lado é o guarda de drift (hoje **45**: +2 do Item 545 em 24/Out/2026).
 */
export const ATTENTION_MAP = {
    // ── urgent (time-sensitive · lume-urgent) ──
    'handoff-requested': urgent('lume.handoff'),
    'conversation-assigned': urgent(),
    'sla-breach': urgent(),
    'p1-elevated': urgent(),
    'p1-bypassed-attendant-offline': urgent(),
    'p1-volume-critical': urgent(),
    'runbook-escalation': urgent(),
    'runbook-csam-detected': urgentCompliance,
    'runbook-threat-detected': urgent(),
    'runbook-incident-massive': urgent(),
    'runbook-lgpd-critical': urgentCompliance,
    'runbook-anonymous-report': urgent(),
    'dual-approval-pending': urgent(),
    // IA como serviço, S2 — o saldo de IA a 95% e o esgotado (vide o bloco 'work' abaixo
    // para o porquê de estarem no mapa sem push).
    'ai-wallet-warning-95': urgent(),
    'ai-wallet-empty': urgent(),
    // Item 545 (24/Out/2026) — a integração caiu e a IA ficou sem acesso àquele sistema: mesma classe
    // do saldo esgotado (a IA perdeu uma capacidade). Sem push no template (só in_app + e-mail).
    'integration-unavailable': urgent(),
    // ── work (active · lume-work) ──
    'direct-message-received': work,
    'conversation-reassigned-from': work,
    'handoff-accepted': work,
    'force-takeover-executed': work,
    'force-takeover-returned': work,
    'force-takeover-transferred': work,
    'force-playbook-switched': work,
    'override-pending': work,
    'dual-approval-expired': work,
    'pendency-overdue-warning': work,
    'conflict-alert-detected': work,
    // Frente engine-led S7 (Condução Governada) — cliente falou de novo numa
    // conversa atribuída enquanto o operador responsável não a tem aberta. 'work'
    // (não 'urgent'): é um nudge do próprio trabalho do operador, suprimível fora
    // do turno/expediente (honra operatorNotificationPolicy). Gated opt-in
    // `conversation-viewing-awareness`.
    'conversation-customer-waiting': work,
    // Reconciliação do drift declarado em 16/Set/2026 (o guard de paridade estava vermelho): o
    // cliente reabriu uma conversa fechada — é trabalho do operador, com push no seed.
    'conversation-reopened-by-customer': work,
    // IA como serviço, S2 «carteira e corte» — avisos do saldo de IA ao ADMIN do tenant.
    // Estão no mapa por PARIDADE (o guard exige entrada para todo slug do seed), não para
    // ganhar push: o seed declara só `in_app`+`email`, porque a audiência é o admin do
    // tenant e não o operador no celular. Se um dia o push for ligado, o tier já está
    // decidido: a carga lançada e o primeiro alerta são trabalho ('work'); o «quase no fim»
    // e o «parou de responder» interrompem, porque a IA já parou ou está a ponto de parar.
    'ai-wallet-warning-80': work,
    'ai-wallet-credited': work,
    // Item 545 — a integração voltou (em geral pela reconexão automática): par do «crédito lançado».
    'integration-restored': work,
    // Auditoria de cobrança (spec 2026-09-21): vermelho é trabalho da gestão da plataforma.
    'ai-billing-audit-red': work,
    // ── ambient (passive · lume-ambient · NÃO dispara push na v1) ──
    'insight-generated': ambient,
    'quality-coach-scheduled': ambient,
    'override-approved': ambient,
    'override-rejected': ambient,
    'override-frequency-anomaly': ambient,
    'override-temporary-feature-flag-expiring-soon': ambient,
    'conflict-alert-mitigated': ambient,
    'behavioral-contestation-pending': ambient,
    'behavioral-contestation-reviewed': ambient,
    'pendency-bulk-reassigned': ambient,
    // Reconciliação do drift de 16/Set/2026: checklist parada — informacional, sem push no seed.
    'checklist-stale-warning': ambient,
    // Política de preço da plataforma (spec 2026-09-21, D8): mudança de tabela de preço é
    // informacional e contratual. Princípio 3 — informacional nunca interrompe.
    'ai-price-reprice-scheduled': ambient,
};
export function resolveAttentionDelivery(slug) {
    return ATTENTION_MAP[slug] ?? null;
}
/**
 * Keys de payload que identificam a ENTIDADE para coalescing. Ordem = precedência:
 * as entidades ESPECÍFICAS vêm antes de `conversationId`/`threadId` — assim um
 * template de override/aprovação/alerta que um dia passe a carregar `conversationId`
 * no payload ainda colapsa pela sua entidade real (2 overrides na mesma conversa não
 * viram 1 banner). Colapsa apenas `slug:entidade` idênticos (mesmo template + mesma
 * entidade) — nunca mistura templates. Sem entidade resolvível ⇒ null (não fabrica).
 */
const COLLAPSE_ENTITY_KEYS = [
    'approvalId',
    'overrideId',
    'alertId',
    'incidentId',
    'patternId',
    'contestationId',
    'pendencyId',
    'conversationId',
    'threadId',
];
export function resolvePushCollapseId(slug, payload) {
    for (const key of COLLAPSE_ENTITY_KEYS) {
        const v = payload[key];
        if (typeof v === 'string' && v.length > 0) {
            return `${slug}:${v}`;
        }
    }
    return null;
}
//# sourceMappingURL=attention-map.js.map